/**
 * Parses DMP document metadata from EfficienceWeb DMPDocument notification
 */
export interface DocumentMetadata {
  uniqueId: string
  title: string
  typeCode: string
  format: string
  contentBase64: string
}

export function parseDocumentMetadata(xmlString: string): DocumentMetadata {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')

  const uniqueId = doc.querySelector('uniqueId')?.textContent || ''
  const title = doc.querySelector('title')?.textContent || 'Untitled Document'
  const typeCode = doc.querySelector('typeCode')?.textContent || ''
  const format = doc.querySelector('format')?.textContent || ''
  const contentBase64 = doc.querySelector('content')?.textContent || ''

  return { uniqueId, title, typeCode, format, contentBase64 }
}

/**
 * Parses multiple DMP documents from DMPDocuments notification.
 * Each <document> inside <documents> has the same structure as DMPDocument.
 */
export function parseMultipleDocumentsMetadata(xmlString: string): DocumentMetadata[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')

  const documentElements = doc.querySelectorAll('documents > document')
  return Array.from(documentElements).map((docEl) => ({
    uniqueId: docEl.querySelector('uniqueId')?.textContent || '',
    title: docEl.querySelector('title')?.textContent || 'Untitled Document',
    typeCode: docEl.querySelector('typeCode')?.textContent || '',
    format: docEl.querySelector('format')?.textContent || '',
    contentBase64: docEl.querySelector('content')?.textContent || '',
  }))
}

/**
 * Parses email attachments from MSS email notification.
 */
export interface EmailAttachment {
  fileName: string
  contentType: string
  size: number
  contentBase64: string
}

export function parseEmailAttachments(xmlString: string): EmailAttachment[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')

  const attachmentElements = doc.querySelectorAll('attachments > attachment')
  return Array.from(attachmentElements).map((el) => ({
    fileName: el.querySelector('fileName')?.textContent || 'attachment',
    contentType: el.querySelector('contentType')?.textContent || 'application/octet-stream',
    size: parseInt(el.querySelector('size')?.textContent || '0', 10),
    contentBase64: el.querySelector('content')?.textContent || '',
  }))
}

/**
 * Extracts DMP status from testDmpExistence response
 * @returns "open" | "closed" | "no-dmp" | "error"
 */
export function extractDmpStatus(xmlString: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')

  const dmpElement = doc.querySelector('dmp')
  if (!dmpElement) return 'error'

  return dmpElement.getAttribute('status') || 'error'
}

/**
 * Checks if XML response indicates success
 */
export function isSuccessResponse(xmlString: string): boolean {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')

  const answerElement = doc.querySelector('answer')
  if (!answerElement) return false

  const status = answerElement.getAttribute('status') || ''
  return status.includes('Success')
}

/**
 * Converts base64 document content to File object
 */
const FORMAT_TO_MIME: Record<string, { mimeType: string; extension: string }> = {
  pdf: { mimeType: 'application/pdf', extension: '.pdf' },
  text: { mimeType: 'text/plain', extension: '.txt' },
  jpeg: { mimeType: 'image/jpeg', extension: '.jpeg' },
  jpg: { mimeType: 'image/jpeg', extension: '.jpg' },
  tiff: { mimeType: 'image/tiff', extension: '.tiff' },
  rtf: { mimeType: 'application/rtf', extension: '.rtf' },
}

/**
 * Detects MIME type and extension from file content magic bytes.
 * Used as fallback when the format field is non-standard.
 */
function detectMimeFromContent(bytes: Uint8Array): { mimeType: string; extension: string } | null {
  if (bytes.length < 4) return null

  // PDF: %PDF
  if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return { mimeType: 'application/pdf', extension: '.pdf' }
  }

  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return { mimeType: 'image/jpeg', extension: '.jpeg' }
  }

  // TIFF: II (little-endian) or MM (big-endian)
  if ((bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00) ||
      (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a)) {
    return { mimeType: 'image/tiff', extension: '.tiff' }
  }

  // RTF: {\rtf
  if (bytes[0] === 0x7b && bytes[1] === 0x5c && bytes[2] === 0x72 && bytes[3] === 0x74) {
    return { mimeType: 'application/rtf', extension: '.rtf' }
  }

  // Check text-based formats by decoding the first bytes
  const header = new TextDecoder('utf-8').decode(bytes.slice(0, 100)).trimStart()

  // HTML: <!DOCTYPE or <html
  if (header.toLowerCase().startsWith('<!doctype html') || header.toLowerCase().startsWith('<html')) {
    return { mimeType: 'text/html', extension: '.html' }
  }

  // XML: <?xml
  if (header.startsWith('<?xml')) {
    return { mimeType: 'application/xml', extension: '.xml' }
  }

  return null
}

export function base64ToFile(base64: string, filename: string, format: string): File {
  const normalizedFormat = format.toLowerCase()
  const formatMatch = Object.entries(FORMAT_TO_MIME).find(([key]) => normalizedFormat.includes(key))

  // Decode base64
  const byteString = atob(base64)
  const arrayBuffer = new ArrayBuffer(byteString.length)
  const uint8Array = new Uint8Array(arrayBuffer)

  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i)
  }

  // Use format field first, fall back to content detection
  const detected = formatMatch ? formatMatch[1] : detectMimeFromContent(uint8Array)
  if (!detected) {
    throw new Error(`Unsupported document format: "${format}"`)
  }

  const allExtensions = [...Object.values(FORMAT_TO_MIME).map((v) => v.extension), detected.extension]
  const hasValidExtension = allExtensions.some((ext) => filename.toLowerCase().endsWith(ext))
  const fullFilename = hasValidExtension ? filename : filename + detected.extension

  return new File([uint8Array], fullFilename, { type: detected.mimeType })
}
